import InternalAudit, {
  AuditMeetingAvailability,
  AuditMeetingSlot,
  AuditParticipantAvailability,
  InternalAuditLink,
  InternalAuditSaveAsTemplateResult,
  InternalAuditTask,
  InternalAuditUser,
} from 'models/internalAudit';
import InternalAuditDTO, {
  InternalAuditLinkDTO,
  InternalAuditMeetingAvailabilityDTO,
  InternalAuditMeetingSlotDTO,
  InternalAuditParticipantAvailabilityDTO,
  InternalAuditSaveAsTemplateResultDTO,
  InternalAuditTaskDTO,
  InternalAuditUserDTO,
} from 'models/dto/internalAuditDTO';
import { fromApiDateTimeOptional, toApiDateTimeOptional } from 'utils/datetime';

export function mapFromInternalAuditUser(dto: InternalAuditUserDTO): InternalAuditUser {
  const item = new InternalAuditUser();
  item.internalAuditId = dto.internalAuditId;
  item.userId = dto.userId;
  item.role = dto.role;

  return item;
}

export function mapToInternalAuditUsers(items: InternalAuditUser[]): InternalAuditUserDTO[] {
  return (items ?? []).map((u) => {
    const dto = new InternalAuditUserDTO();
    dto.internalAuditId = u.internalAuditId;
    dto.userId = u.userId;
    dto.role = u.role;

    return dto;
  });
}

export function mapFromInternalAuditTask(dto: InternalAuditTaskDTO): InternalAuditTask {
  const item = new InternalAuditTask();
  item.internalAuditId = dto.internalAuditId;
  item.taskId = dto.taskId;

  return item;
}

export function mapFromInternalAuditLink(dto: InternalAuditLinkDTO): InternalAuditLink {
  const item = new InternalAuditLink();
  item.internalAuditId = dto.internalAuditId;
  item.linkId = dto.linkId;

  return item;
}

export function mapFromInternalAudit(dto: InternalAuditDTO): InternalAudit {
  const item = new InternalAudit();
  item.tenantId = dto.tenantId;
  item.internalAuditId = dto.internalAuditId;
  item.internalAuditTemplateId = dto.internalAuditTemplateId;
  item.name = dto.name;
  item.status = dto.status;
  item.origin = dto.origin;
  item.startDate = fromApiDateTimeOptional(dto.startDate);
  item.endDate = fromApiDateTimeOptional(dto.endDate);
  item.conclusion = dto.conclusion;
  item.meetingStart = fromApiDateTimeOptional(dto.meetingStart);
  item.meetingEnd = fromApiDateTimeOptional(dto.meetingEnd);
  item.meetingEventId = dto.meetingEventId;
  item.meetingOrganizerUserId = dto.meetingOrganizerUserId;
  item.meetingJoinUrl = dto.meetingJoinUrl;
  item.meetingCreated = fromApiDateTimeOptional(dto.meetingCreated);
  item.meetingUpdated = fromApiDateTimeOptional(dto.meetingUpdated);
  item.meetingIsStale = dto.meetingIsStale ?? false;
  item.invitationSent = fromApiDateTimeOptional(dto.invitationSent);
  item.finalInvitationSent = fromApiDateTimeOptional(dto.finalInvitationSent);
  item.authSchemaId = dto.authSchemaId;
  item.reportLinkId = dto.reportLinkId;
  item.reportPdfLinkId = dto.reportPdfLinkId;
  item.reportGeneratedAtStatus = dto.reportGeneratedAtStatus;
  item.reportGeneratedOn = fromApiDateTimeOptional(dto.reportGeneratedOn);
  item.commentTrailId = dto.commentTrailId;
  item.auditTrailId = dto.auditTrailId;
  item.modified = fromApiDateTimeOptional(dto.modified);
  item.users = (dto.users ?? []).map(mapFromInternalAuditUser);
  item.tasks = (dto.tasks ?? []).map(mapFromInternalAuditTask);
  item.links = (dto.links ?? []).map(mapFromInternalAuditLink);
  // null and absent both mean "not loaded": never coerced to [], which would read as "no standards"
  item.normIds = dto.normIds ?? undefined;

  return item;
}

export function mapFromInternalAudits(dtos: InternalAuditDTO[] | undefined): InternalAudit[] {
  if (!dtos) return [];

  return dtos.map(mapFromInternalAudit);
}

function mapFromAuditMeetingSlot(dto: InternalAuditMeetingSlotDTO): AuditMeetingSlot {
  const item = new AuditMeetingSlot();
  item.start = fromApiDateTimeOptional(dto.start);
  item.end = fromApiDateTimeOptional(dto.end);

  return item;
}

function mapFromAuditParticipantAvailability(
  dto: InternalAuditParticipantAvailabilityDTO,
): AuditParticipantAvailability {
  const item = new AuditParticipantAvailability();
  item.userId = dto.userId;
  item.name = dto.name;
  item.role = dto.role;
  item.required = dto.required;
  item.status = dto.status;
  item.conflicts = (dto.conflicts ?? []).map(mapFromAuditMeetingSlot);

  return item;
}

export function mapFromAuditMeetingAvailability(dto: InternalAuditMeetingAvailabilityDTO): AuditMeetingAvailability {
  const item = new AuditMeetingAvailability();
  item.start = fromApiDateTimeOptional(dto.start);
  item.end = fromApiDateTimeOptional(dto.end);
  item.participants = (dto.participants ?? []).map(mapFromAuditParticipantAvailability);
  item.suggestedSlots = (dto.suggestedSlots ?? []).map(mapFromAuditMeetingSlot);
  item.hasConflicts = dto.hasConflicts;

  return item;
}

export function mapFromInternalAuditSaveAsTemplateResult(
  dto: InternalAuditSaveAsTemplateResultDTO,
): InternalAuditSaveAsTemplateResult {
  const item = new InternalAuditSaveAsTemplateResult();
  item.internalAuditTemplateId = dto.internalAuditTemplateId;
  item.pendingAuditId = dto.pendingAuditId;
  item.pendingAuditName = dto.pendingAuditName;

  return item;
}

export function mapToInternalAudit(item: InternalAudit): InternalAuditDTO {
  const dto = new InternalAuditDTO();
  dto.tenantId = item.tenantId;
  dto.internalAuditId = item.internalAuditId;
  dto.internalAuditTemplateId = item.internalAuditTemplateId;
  dto.name = item.name;
  dto.status = item.status;
  dto.origin = item.origin;
  dto.startDate = toApiDateTimeOptional(item.startDate);
  dto.endDate = toApiDateTimeOptional(item.endDate);
  // the planned meeting moment travels back: it is user data, unlike the rest of the meeting state
  dto.meetingStart = toApiDateTimeOptional(item.meetingStart);
  dto.meetingEnd = toApiDateTimeOptional(item.meetingEnd);
  dto.conclusion = item.conclusion;
  dto.authSchemaId = item.authSchemaId;
  dto.commentTrailId = item.commentTrailId;
  dto.auditTrailId = item.auditTrailId;
  dto.tasks = [];
  dto.links = [];
  dto.users = mapToInternalAuditUsers(item.users);
  // undefined stays undefined: the server leaves the standards untouched when they are not sent
  dto.normIds = item.normIds;

  return dto;
}
